import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatDate(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("es-AR", {
      day: "2-digit", month: "short", year: "numeric",
    }).format(d);
  } catch (_) {
    return iso;
  }
}

export function formatDateTime(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("es-AR", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(d);
  } catch (_) {
    return iso;
  }
}

export function ageMonths(birthDate) {
  if (!birthDate) return null;
  try {
    const d = new Date(birthDate);
    const now = new Date();
    const months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
    return months;
  } catch (_) { return null; }
}

export function ageLabel(birthDate) {
  const m = ageMonths(birthDate);
  if (m === null) return "-";
  if (m < 12) return `${m} meses`;
  const years = Math.floor(m / 12);
  const remM = m % 12;
  const yearsLabel = years === 1 ? "año" : "años";
  return remM === 0 ? `${years} ${yearsLabel}` : `${years} ${yearsLabel} ${remM} m`;
}

// Fix E (2026-08-24): rangos etarios definidos por el cliente.
//   Hembras: 0-8 becerra · 9-17 mauta · 18-24 novilla · 25+ vaca
//   Machos:  0-8 becerro · 9-24 maute · 25+ toro
// Los bordes 18 y 25 se resuelven sin superposición: 18 ya es novilla y 25 ya es
// vaca/toro. Esta función DEBE quedar idéntica a derive_category() del backend
// (backend/app/routers/animals.py), o el mismo animal quedaría en categorías
// distintas según dónde se calcule.
export function deriveCategory(sex, birthDate) {
  const m = ageMonths(birthDate);
  if (m === null) return null;
  if (sex === "female") {
    if (m < 9) return "ternera";       // Becerra
    if (m < 18) return "mauta";        // Mauta
    if (m < 25) return "vaquillona";   // Novilla
    return "vaca";
  } else {
    if (m < 9) return "ternero";       // Becerro
    if (m < 25) return "novillo";      // Maute
    return "toro";
  }
}

// Grupo etario — Bovino (valores internos sin cambios, solo se renombraron las etiquetas)
export const CATEGORIES_BOVINO = [
  { value: "vaca", label: "Vaca", group: "bovino" },
  { value: "vaquillona", label: "Novilla", group: "bovino" },
  { value: "ternera", label: "Becerra", group: "bovino" },
  { value: "toro", label: "Toro", group: "bovino" },
  { value: "novillo", label: "Maute", group: "bovino" },
  { value: "ternero", label: "Becerro", group: "bovino" },
  // Fix A (2026-08-24): faltaba la hembra joven del ciclo (becerra → MAUTA → novilla
  // → vaca). Se agrega como value nuevo 'mauta'; NO se renombra ningún value
  // existente porque son identificadores ya guardados en la base.
  { value: "mauta", label: "Mauta", group: "bovino" },
];

// Grupo etario — Bufalino
export const CATEGORIES_BUFALINO = [
  { value: "bufala", label: "Búfala", group: "bufalino" },
  { value: "novilla_bufalina", label: "Novilla bufalina", group: "bufalino" },
  { value: "becerra_bufalina", label: "Becerra bufalina", group: "bufalino" },
  { value: "bufalo", label: "Búfalo", group: "bufalino" },
  { value: "maute_bufalino", label: "Maute bufalino", group: "bufalino" },
  { value: "becerro_bufalino", label: "Becerro bufalino", group: "bufalino" },
  // Fix A (2026-08-24): equivalente hembra, por simetría con el grupo bovino.
  { value: "mauta_bufalina", label: "Mauta bufalina", group: "bufalino" },
];

export const CATEGORIES = [...CATEGORIES_BOVINO, ...CATEGORIES_BUFALINO];

export const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

export const CATEGORY_COLORS = {
  vaca: "hsl(158 64% 24%)",
  vaquillona: "hsl(174 60% 38%)",
  ternera: "hsl(43 96% 45%)",
  toro: "hsl(222 47% 35%)",
  novillo: "hsl(205 78% 42%)",
  ternero: "hsl(25 95% 55%)",
  bufala: "hsl(280 40% 35%)",
  novilla_bufalina: "hsl(280 35% 50%)",
  becerra_bufalina: "hsl(280 45% 65%)",
  bufalo: "hsl(260 35% 28%)",
  maute_bufalino: "hsl(265 40% 45%)",
  becerro_bufalino: "hsl(270 45% 60%)",
  // Fix A (2026-08-24): colores de las categorías nuevas. Sin esto, el gráfico
  // de distribución del dashboard las dibuja sin color asignado.
  mauta: "hsl(190 70% 40%)",
  mauta_bufalina: "hsl(295 40% 52%)",
  sin_categoria: "hsl(215 16% 60%)",
};

// Días restantes hasta una fecha ISO (negativo si ya pasó — "vencida")
export function daysUntil(isoDate) {
  if (!isoDate) return null;
  return Math.ceil((new Date(isoDate).getTime() - Date.now()) / 86400000);
}

// Etiqueta de mes/año para agrupar (ej. "Nacimientos previstos")
export function monthLabel(isoDate) {
  if (!isoDate) return "Sin fecha estimada";
  try {
    const label = new Intl.DateTimeFormat("es-AR", { month: "long", year: "numeric" }).format(new Date(isoDate));
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch (_) {
    return "Sin fecha estimada";
  }
}
